import { useCustom } from "@refinedev/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";

import { PlanDashboardPage } from "@/pages/PlanDashboardPage";

import { PlansTable, type PlanTotalsPatch } from "@/components/PlansTable";
import { Segmented } from "@/components/Segmented";
import { query } from "@/lib/api";
import { patchParams } from "@/lib/params";
import { groupPlans, pickedProducts, smartIdsOfPlans } from "@/lib/plans";
import { LIST_PARAM } from "@/lib/productNav";
import type { PlanRow } from "@/lib/providers";
import type { BatchStatusSaved } from "@/components/plans/types";
import { speedWindow } from "@/lib/reorder";
import { PlansFooter } from "./plans/PlansFooter";
import { PlansHeader } from "./plans/PlansHeader";
import { PlansPickBar } from "./plans/PlansPickBar";
import { PlansToolbar } from "./plans/PlansToolbar";
import { тристорона, поставщики } from "./plans/filters";
import type { FiltersPayload, PlansPayload } from "./plans/types";
import { usePlanActions } from "./plans/usePlanActions";

type Вкладка = "list" | "dashboard";

/** Потолок отбора `plan_ids` — тот же, что `PLAN_IDS_MAX` сервера. */
const PLAN_IDS_MAX = 500;

/**
 * Вкладки раздела: «Список» — без параметра, «Дашборд» — `?tab=dashboard`,
 * как у «Заказов». Дашборд переехал сюда с `/dashboard/plans` (28.09.26,
 * просьба владельца); отбор по дате создания у обоих под одними ключами
 * `cfrom`/`cto`, поэтому переключение вкладки его не теряет.
 */
export function PlansPage() {
  const [params, setParams] = useSearchParams();
  const вкладка: Вкладка = params.get("tab") === "dashboard" ? "dashboard" : "list";
  const выбрать = (v: Вкладка) => {
    const next = new URLSearchParams(params);
    if (v === "dashboard") next.set("tab", "dashboard");
    else next.delete("tab");
    setParams(next, { replace: true });
  };
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-none items-center gap-2 px-5 pt-4">
        <Segmented<Вкладка>
          label="вид"
          options={[
            { value: "list", label: "Список" },
            { value: "dashboard", label: "Дашборд" },
          ]}
          isOn={(v) => v === вкладка}
          onPick={выбрать}
        />
      </div>
      {вкладка === "dashboard" ? (
        <div className="min-h-0 flex-1 overflow-y-auto px-2">
          <PlanDashboardPage />
        </div>
      ) : (
        <PlansList />
      )}
    </div>
  );
}

function PlansList() {
  // Отбор живёт в адресе, а не в состоянии компонента: ссылку на отбор можно
  // переслать коллеге и открыть завтра, и она покажет то же самое.
  const [params, setParams] = useSearchParams();

  // По образцу, не по снимку: дебаунс поиска пишет адрес через 350 мс, и снимок
  // того рендера затирал бы чип, кликнутый за это время (`lib/params.ts`).
  const set = (key: string, value: string) =>
    setParams((prev) => patchParams(prev, { [key]: value }), { replace: true });

  // ДЕФОЛТНЫЙ СТАТУС ЖИВЁТ В АДРЕСЕ, а не в запросе (замечание владельца
  // 16.09.26): экран, молча показывающий часть партий, неотличим от
  // сломанного. Переписав адрес, мы делаем отбор видимым в списке «статус» и
  // снимаемым первой его строкой. Замер `crm-prod` 16.09.26: партий 3 392,
  // активных 364 — без дефолта экран открывался историей за полтора года.
  useEffect(() => {
    if (params.has("status")) return;
    const next = new URLSearchParams(params);
    next.set("status", "1");
    setParams(next, { replace: true });
  }, [params, setParams]);

  // Диапазон — ОДНА правка адреса, а не два `set` подряд: второй читал бы
  // `params` до первой правки и затирал бы её.
  const setRange = (from: string, to: string) =>
    setParams((prev) => patchParams(prev, { from, to }), { replace: true });

  // Дата создания правится тем же приёмом и по той же причине: пара границ —
  // ОДНА запись адреса.
  const setCreatedRange = (from: string, to: string) =>
    setParams((prev) => patchParams(prev, { cfrom: from, cto: to }), { replace: true });

  // Поиск набирают посимвольно, а за каждым запросом стоит ЧУЖАЯ боевая база.
  // Поэтому поле держит своё состояние, а в адрес (и в запрос) уезжает только
  // то, что человек допечатал.
  const urlSearch = params.get("q") ?? "";
  const [searchDraft, setSearchDraft] = useState(urlSearch);
  useEffect(() => setSearchDraft(urlSearch), [urlSearch]);
  useEffect(() => {
    if (searchDraft === urlSearch) return;
    const timer = setTimeout(() => set("q", searchDraft), 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft]);

  // Отмеченные партии из адреса (`ids=`) разбираются ОДИН раз: этим же списком
  // уходит отбор и считается чип. Только цифры, без повторов; мусор и пустые
  // места отбрасываются: чип считает ровно то, что ушло в отбор, а не запятые.
  // Ноль партией не бывает (сервер ответил бы 422 всему экрану) — выпадает; больше
  // `PLAN_IDS_MAX` номеров — отбора нет вовсе, и чипа тоже: экран живёт, а не 422.
  const idsParam = params.get("ids") ?? "";
  const planIds = useMemo(() => {
    const ids = [
      ...new Set(
        idsParam
          .split(",")
          .map((one) => one.trim())
          .filter((one) => /^\d+$/.test(one))
          .map(Number)
          .filter((id) => id > 0),
      ),
    ];
    return ids.length > PLAN_IDS_MAX ? [] : ids;
  }, [idsParam]);

  const apiParams = useMemo(
    () => ({
      // `all` — ЯВНОЕ «все статусы», отличимое от «параметра нет»: первое
      // ставит человек первой строкой списка, второе бывает только у старой
      // ссылки, и ей дефолт подставляется выше.
      status_id: (params.get("status") ?? "") === "all" ? "" : (params.get("status") ?? ""),
      produce_status_id: params.get("produce") ?? "",
      // Несколько фабрик — через запятую, так их уносит «Открыть» дашборда.
      supplier_id: поставщики(params.get("supplier")),
      category_id: params.get("cat") ?? "",
      qr_id: params.get("qr") ?? "",
      has_qr: тристорона(params.get("qrhas")),
      has_card: тристорона(params.get("card")),
      is_paid: тристорона(params.get("paid")),
      date_from: params.get("from") ?? "",
      date_to: params.get("to") ?? "",
      created_from: params.get("cfrom") ?? "",
      created_to: params.get("cto") ?? "",
      search: urlSearch,
      // Отбор, пришедший ссылкой с карточки товара: одна партия, товар
      // целиком или один его размер (решение 2 спеки «Планирование переезжает
      // в карточку товара»). `size` в API не уходит — это подпись чипа ниже,
      // а не параметр отбора: у `/api/plans` такого параметра нет вовсе.
      product_id: params.get("product_id") ?? "",
      card_id: params.get("card_id") ?? "",
      plan_id: params.get("plan_id") ?? "",
      // Отмеченные планы, открытые отдельным окном из «открыть ▾» полосы выбора.
      // Пустой список не уходит вовсе: пустую строку `query()` выбрасывает.
      plan_ids: planIds.length ? planIds.join(",") : "",
      // Экран обещает «страниц нет» — значит отбор обязан приезжать ЦЕЛИКОМ,
      // иначе обещание враньё: на 3158 партиях срез в 3000 молча прятал 158.
      // Потолок 20 000 — замеренная граница, за которой браузеру уже тяжело
      // фильтровать локально; шире отбор уходит на сервер.
      limit: 20000,
    }),
    [params, urlSearch, planIds],
  );

  // Подпись чипа отбора, пришедшего с карточки товара. `plan_id` называет одну
  // партию — искать размер внутри неё нечем и незачем, `/api/plans` вернёт её
  // ОДНОЙ строкой независимо от размера. `card_id` без имени размера в адресе
  // не хватило бы: у `/api/plans` отбор идёт номером карточки, а не подписью,
  // поэтому подпись едет отдельным ключом `size` (URL-кодированным на
  // карточке товара) и читается здесь ТОЛЬКО для текста чипа.
  const scopeProductId = params.get("product_id");
  const scopeCardId = params.get("card_id");
  const scopePlanId = params.get("plan_id");
  const scopeSize = params.get("size");
  const scopeLabel = scopePlanId
    ? `партия ${scopePlanId}`
    : scopeCardId
      ? `товар ${scopeProductId} · размер${scopeSize ? ` ${scopeSize}` : ""}`
      : scopeProductId
        ? `товар ${scopeProductId}`
        : null;
  const clearScope = () => {
    const next = new URLSearchParams(params);
    ["product_id", "card_id", "plan_id", "size"].forEach((key) =>
      next.delete(key),
    );
    setParams(next, { replace: true });
  };

  const idsCount = planIds.length;

  const plans = useCustom<PlansPayload>({
    url: `/api/plans${query(apiParams)}`,
    method: "get",
  });
  const dict = useCustom<FiltersPayload>({
    url: "/api/plans/filters",
    method: "get",
  });

  const isFetching = plans.query.isFetching;
  // ИТОГИ ПРАВЛЕННЫХ ПАРТИЙ ложатся ПОВЕРХ ответа: правка размера меняет план
  // партии, и свёрнутая строка без этого спорила бы с составом, который
  // закупщик только что поправил. Перезапрашивать ради одного числа все
  // 20 000 партий нельзя — ответ ручки уже принёс новые итоги.
  // Поверх ложатся и итоги после правки размера, и статус после смены статуса
  // партии — одной картой по номеру партии, слиянием, а не заменой.
  const [patched, setPatched] = useState<Record<number, Partial<PlanRow>>>({});
  const rows = useMemo(() => {
    const base = plans.result.data?.rows ?? [];
    if (Object.keys(patched).length === 0) return base;
    return base.map((row) => (patched[row.id] ? { ...row, ...patched[row.id] } : row));
  }, [plans.result.data, patched]);

  // Наложение снимается на каждом свежем ответе: сервер уже вернул правленые
  // числа, и держать поверх них свою копию значило бы завести второе место
  // правды.
  useEffect(() => setPatched({}), [plans.result.data]);
  const totals = plans.result.data?.totals;
  const suppliers = dict.result.data?.suppliers ?? [];
  const набор = поставщики(params.get("supplier"));
  const statuses = dict.result.data?.statuses ?? [];
  const produceStatuses = dict.result.data?.produce_statuses ?? [];
  const categories = dict.result.data?.categories ?? [];

  const folders = plans.result.data?.folders;
  const groups = useMemo(() => groupPlans(rows, folders), [rows, folders]);

  // Выбор живёт в состоянии страницы, ключ — номер партии. В адрес он не едет:
  // отмеченные партии — черновик одного прохода по карточкам, а не отбор.
  const [picked, setPicked] = useState<ReadonlySet<number>>(() => new Set());

  // Свёртка групп — счётчик нажатий, а не флаг: состояние групп живёт в
  // таблице, и второй его хозяин на странице разъехался бы с первым.
  const [foldAll, setFoldAll] = useState(0);
  const [unfoldAll, setUnfoldAll] = useState(0);

  // Окно уходимости читает ОБЩАЯ `speedWindow`, та же, что у карточки товара
  // и кольца листания: чужое значение в адресе (`?w=42`) откатывается к 60, а
  // второй такой разбор разъехался бы с ними на первой правке списка окон.
  const windowDays = speedWindow(params);

  // При смене отбора выбор ПЕРЕСЕКАЕТСЯ с новыми строками: открыть товар,
  // которого на экране уже нет, значило бы открыть не то, что видно отмеченным.
  useEffect(() => {
    setPicked((prev) => {
      if (prev.size === 0) return prev;
      const live = new Set(rows.map((row) => row.id));
      const next = new Set([...prev].filter((id) => live.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [rows]);

  const pick = useCallback((planIds: number[], on: boolean) => {
    setPicked((prev) => {
      const next = new Set(prev);
      for (const id of planIds) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);

  // «Отметить все» (просьба владельца 23.10.26) — весь отбор: строки
  // приезжают целиком, страниц у экрана нет.
  const pickAll = useCallback(() => setPicked(new Set(rows.map((row) => row.id))), [rows]);

  const products = useMemo(() => pickedProducts(groups, picked), [groups, picked]);

  // Новая вкладка: «Планы» с выбором остаются на месте — смысл в том, чтобы
  // пройти по карточкам и вернуться к планам. Настоящая ссылка, а не
  // `window.open`: Ctrl-клик и средняя кнопка работают сами, как у ячеек
  // «в планах» на карточке товара.
  const openHref = `/product/${products[0] ?? ""}?${LIST_PARAM}=${products.join(",")}`;

  // «Отмеченные планы в новом окне»: номера по возрастанию — адрес не зависит
  // от порядка отметок; `status=all`, иначе дефолт «Активный» спрятал бы
  // отмеченные партии в другом статусе.
  const plansHref = `/plans?ids=${[...picked].sort((a, b) => a - b).join(",")}&status=all`;

  // QR-ПАПКУ ПОЛУЧАЮТ РОВНО ОТМЕЧЕННЫЕ ПАРТИИ (решение владельца 15.09.26):
  // отметил группу, снял лишние — привязываются оставшиеся. Папку заводит сама
  // CRM методом «+QR Быстро» от имени вошедшего сотрудника
  // (`app/core/modules/plan/qr.py`).
  const pickedPlans = useMemo(
    () => rows.filter((row) => picked.has(row.id)),
    [rows, picked],
  );
  // Порядок ОТМЕТОК, а не таблицы (`Set` помнит порядок добавления): номера в
  // меню CRM и в файле идут так, как закупщик их отмечал.
  const pickedInOrder = useMemo(() => {
    const byId = new Map(rows.map((row) => [row.id, row]));
    return [...picked].flatMap((id) => byId.get(id) ?? []);
  }, [rows, picked]);
  const smartIds = useMemo(() => smartIdsOfPlans(pickedInOrder), [pickedInOrder]);

  // `memo(PlansTable)` работает, только пока пропсы стабильны: стрелка на
  // каждый рендер и литерал `groupQr` перерисовывали бы таблицу вместе с
  // виртуализатором на каждую букву в поиске (аудит 12.10.26, B47). `createQr`
  // читает отмеченные и справочники — свежая версия берётся через ссылку.
  const onLineSaved = useCallback(
    (planId: number, totals: PlanTotalsPatch) =>
      setPatched((prev) => ({ ...prev, [planId]: { ...prev[planId], ...totals } })),
    [],
  );
  // `status_is_active` — признак СПРАВОЧНИКА статусов, а не «равен единице»;
  // справочник берётся через ссылку, чтобы колбэк оставался стабильным для memo.
  const statusesRef = useRef(statuses);
  statusesRef.current = statuses;
  const onBatchSaved = useCallback(
    (result: BatchStatusSaved) =>
      setPatched((prev) => ({
        ...prev,
        [result.plan_id]: {
          ...prev[result.plan_id],
          status_id: result.status_id,
          status_name: result.status_name,
          status_is_active:
            statusesRef.current.find((s) => s.id === result.status_id)?.is_active ?? result.status_id === 1,
        },
      })),
    [],
  );
  const {
    qrWhy,
    qrBusy,
    qrNote,
    createQr,
    addToQr,
    groupQr,
    bulk,
    wechatWhy,
    wechatBusy,
    wechatReload,
    sendWechat,
    dateWhy,
    dateOpen,
    setDateOpen,
    dateBusy,
    moveDate,
  } = usePlanActions({
    dictData: dict.result.data,
    groups,
    pickedPlans,
    picked,
    pick,
    setPicked,
    refetch: plans.query.refetch,
  });

  // НИЖЕ `md` ЭКРАН ПРОКРУЧИВАЕТСЯ САМ. На десктопе прокрутка одна — внутри
  // таблицы, и шапка с отбором стоят перед глазами. На телефоне тот же
  // `overflow-hidden` означал пустоту: отбор разворачивается в восемь рядов и
  // съедает всю высоту, таблице остаётся 2px и ноль строк (замер 13.10.26 на
  // 390×844). Высоту таблице задаёт `max-md:min-h-[70dvh]` в `PlansTable`.
  return (
    <main className="flex min-h-0 flex-1 flex-col overflow-hidden px-5 pt-3 pb-5 max-md:overflow-y-auto max-md:px-3">
      <PlansHeader totals={totals} isFetching={isFetching} />

      <PlansToolbar
        params={params}
        setParams={setParams}
        set={set}
        setRange={setRange}
        setCreatedRange={setCreatedRange}
        searchDraft={searchDraft}
        setSearchDraft={setSearchDraft}
        scopeLabel={scopeLabel}
        clearScope={clearScope}
        idsCount={idsCount}
        clearIds={() => set("ids", "")}
        statuses={statuses}
        produceStatuses={produceStatuses}
        suppliers={suppliers}
        набор={набор}
        categories={categories}
        windowDays={windowDays}
      />

      <PlansPickBar
        picked={picked}
        setPicked={setPicked}
        products={products}
        openHref={openHref}
        plansHref={plansHref}
        dateOpen={dateOpen}
        setDateOpen={setDateOpen}
        dateWhy={dateWhy}
        dateBusy={dateBusy}
        moveDate={moveDate}
        qrWhy={qrWhy}
        qrBusy={qrBusy}
        pickedPlans={pickedPlans}
        pickedInOrder={pickedInOrder}
        createQr={createQr}
        addToQr={addToQr}
        sendWechat={sendWechat}
        wechatWhy={wechatWhy}
        wechatBusy={wechatBusy}
        bulk={bulk}
        total={rows.length}
        pickAll={pickAll}
        smartIds={smartIds}
      />

      <PlansTable
        rows={rows}
        groups={groups}
        loading={isFetching}
        picked={picked}
        onPick={pick}
        foldAll={foldAll}
        unfoldAll={unfoldAll}
        wechatReload={wechatReload}
        windowDays={windowDays}
        writeBlocked={
          dict.result.data ? dict.result.data.write_blocked : "ждём справочники экрана"
        }
        onLineSaved={onLineSaved}
        batchBlocked={
          dict.result.data
            ? (dict.result.data.date_blocked ?? dict.result.data.write_blocked)
            : "ждём справочники экрана"
        }
        onBatchSaved={onBatchSaved}
        groupQr={groupQr}
      />

      <PlansFooter rows={rows} totals={totals} setFoldAll={setFoldAll} setUnfoldAll={setUnfoldAll} qrNote={qrNote} />
    </main>
  );
}
